import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { friendlyName } from './format';
import { useAuthStore } from '../store/authStore';

// Resolves the effective agent_id + display name for the agent portal.
//
// - Admin "Login as" impersonation: use the impersonated agent record.
// - Real agent login: fetch /me, which returns the linked_agent_id (rows and
//   payments are keyed by agent_id, NOT the Cognito sub) and the real display
//   name. Without this, a real agent's user.id is their Cognito sub and the
//   portal would resolve to no data.
//
// The backend independently enforces this scope for non-admins, so this is
// about resolving the right id for cache keys + showing the real name.
export function useMe() {
  const impersonating = useAuthStore((s) => s.impersonating);
  const user = useAuthStore((s) => s.user);
  const realUserIsAdmin = useAuthStore((s) => s.realUser?.role === 'admin');

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    // Only real agent logins need /me. During impersonation the admin token is
    // in play, so /me would return the admin — skip it.
    enabled: !impersonating && !realUserIsAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const agentId =
    impersonating?.agent_id ?? meQuery.data?.linked_agent_id ?? user?.id ?? '';
  const displayName =
    friendlyName(
      impersonating?.display_name ?? meQuery.data?.display_name ?? user?.display_name,
    ) || 'there';

  // Negative balance carried into the next statement. From the impersonated
  // agent record (Login as) or /me (real login).
  const carryForward = Number(impersonating?.carry_forward ?? meQuery.data?.carry_forward ?? 0);

  return { agentId, displayName, carryForward, profile: meQuery.data, isLoading: meQuery.isLoading };
}
